/**
 * dashboardCache — last-known dashboard trips snapshot in localStorage.
 *
 * Used to deliver an instant first paint on Dashboard mount (and on
 * return-to-Dashboard navigations) while the real /dashboard/trips
 * request fires in the background. The fresh response replaces the
 * cached snapshot once it lands — classic stale-while-revalidate.
 *
 * Cache is keyed per user_id so accounts don't cross-contaminate.
 * Entries older than 7 days are discarded as a safety valve in case
 * the schema drifts under stale clients.
 */
const STORAGE_PREFIX = "dashboard-trips-cache:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const safeParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

export const readDashboardTripsCache = (userId) => {
  if (!userId || typeof window === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_PREFIX + userId);
  const parsed = safeParse(raw);
  if (!parsed || !parsed.timestamp || !Array.isArray(parsed.trips)) return null;
  if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
    localStorage.removeItem(STORAGE_PREFIX + userId);
    return null;
  }
  return parsed.trips;
};

export const writeDashboardTripsCache = (userId, trips) => {
  if (!userId || typeof window === "undefined") return;
  if (!Array.isArray(trips)) return;
  try {
    localStorage.setItem(
      STORAGE_PREFIX + userId,
      JSON.stringify({ timestamp: Date.now(), trips }),
    );
  } catch {
    // Quota or serialization failure → silently drop. The dashboard
    // still works without the cache, it just won't be instant on the
    // next cold start.
  }
};

export const clearDashboardTripsCache = (userId) => {
  if (typeof window === "undefined") return;
  if (userId) {
    localStorage.removeItem(STORAGE_PREFIX + userId);
  } else {
    // Wipe all dashboard caches (e.g. on logout).
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  }
};
